import { CampaignTypeWithMappedPurpose } from '@mm/state/selectors/campaignSelectors';
import { cloneDeep, set } from 'lodash';
import { useMemo, useState } from 'react';
import { randomId } from '../../../../../common/helpers/utils';
import { soldConfigInitialValues } from '../AutomationSoLDSettingsSlideout/AutomationSoLDSettingsSlideout';
import {
  getHTMLStringFromState,
  getHTMLText,
} from '../SlideoutEmail/editor/utils';
import { EditorState } from 'prosemirror-state';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useSelector } from 'react-redux';
import { EMAIL_REGEX_DOT_REQUIRED } from '../../../../../common/constants/validations';
import { CampaignType } from '@mm/state/model/CampaignsModel';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { SlideOutMenuType } from '@mm/state/interfaces/view';

export const useSoldEmailNotifications = ({
  campaign,
  menuType,
  campaignId,
}: {
  campaign?: CampaignTypeWithMappedPurpose;
  menuType: SlideOutMenuType;
  campaignId: number;
}) => {
  const getInitialEmails = (campaign?: CampaignTypeWithMappedPurpose) => {
    const to = campaign?.soldConfigurations?.unicorn?.notifications?.email?.to;

    return to?.length
      ? to.map(value => ({
          value,
          id: randomId(),
        }))
      : [{ value: '', id: randomId() }];
  };

  const getInitialState = () =>
    cloneDeep(campaign?.soldConfigurations || soldConfigInitialValues);

  const campaignCRUDActions = useCampaignCRUDActions();

  const initialState = useMemo(() => getInitialState(), [campaign]);

  const chatbotId = useSelector(activeChatbotIdSelector);
  const [content, setContent] = useState<EditorState>();
  const [emails, setEmails] = useState(getInitialEmails(campaign));
  const [subject, setSubject] = useState(
    campaign?.soldConfigurations?.unicorn?.notifications?.email?.subject || '',
  );

  const [busy, setBusy] = useState(false);

  const getPayload = () => {
    const initials = getInitialState();

    if (emails) {
      set(
        initials,
        'unicorn.notifications.email.to',
        emails.map(v => v.value).filter(Boolean),
      );
    }

    if (content) {
      set(
        initials,
        'unicorn.notifications.email.content',
        getHTMLStringFromState(content),
      );
    }
    if (subject) {
      set(initials, 'unicorn.notifications.email.subject', subject);
    }
    set(
      initials,
      'unicorn.notifications.email.active',
      campaign?.soldConfigurations?.unicorn?.notifications?.email?.active ||
        false,
    );
    return initials;
  };

  const payload = getPayload();

  const onClose = () =>
    onBeforeClose(
      initialState.unicorn.notifications.email,
      payload.unicorn.notifications?.email,
    );

  const close = async () => {
    try {
      await onClose();
      closeSlideout();
    } catch (err) {}
  };

  const { close: closeSlideout, onBeforeClose } = useSlideoutOnClose(
    onClose,
    menuType,
  );
  const emailsValid = emails.every(email =>
    EMAIL_REGEX_DOT_REQUIRED.test(email.value.toString().trim()),
  );

  const onSave = async () => {
    setBusy(true);

    const payload = getPayload();

    if (!emailsValid) {
      return errorToast('Some emails are invalid. Please update them.');
    }

    if (!subject) {
      return errorToast('Please enter email subject');
    }

    if (!getHTMLText(payload.unicorn.notifications.email.content)) {
      return errorToast('Please enter email message');
    }

    try {
      if (campaignId) {
        await campaignCRUDActions.updateCampaign(
          {
            id: campaignId,
            soldConfigurations: payload,
          } as unknown as CampaignType,
          chatbotId,
        );

        successToast('Your changes have been saved.', 'top-left');

        closeSlideout();
      }
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  return {
    getInitialEmails,
    getInitialState,
    initialState,
    content,
    setContent,
    emails,
    setEmails,
    subject,
    setSubject,
    getPayload,
    payload,
    onBeforeClose,
    close,
    onSave,
    busy,
  };
};
